import { useTranslation } from 'react-i18next';
import {
  ActionIcon,
  Button,
  Card,
  Checkbox,
  Fieldset,
  Flex,
  Grid,
  NumberInput,
  Select,
  Text,
  TextInput,
  Textarea,
  Title,
  Tooltip,
  Switch,
  Table,
  Group,
  Stack,
  Divider,
  ThemeIcon
} from '@mantine/core';
import { useGetSectionSubcategories, useGetSections } from '../../hooks/sections/queries';
import SelectProductImageModal from './SelectProductImageModal';
import { useDisclosure } from '@mantine/hooks';
import {
  cfdiInvoiceGuideFolder,
  howToSetUpProductVariations,
  outsourcingOrdersGuide
} from '../../utils/guideLinks';
import { Table as MTable } from '@mantine/core';
import { IconBarcode, IconInfoCircle, IconPencil, IconTrash } from '@tabler/icons-react';
import ConfirmationModal from '../../components/ConfirmationModal/ConfirmationModal';
import { useGetSupplies } from '../../hooks/supplies/queries';
import { type ChangeEvent, useEffect, useMemo, useRef, useState } from 'react';
import { showNotification } from '../../utils/Notification/Notification';
import { useSelector } from 'react-redux';
import CRUDCardSkeleton from '../../components/Skeletons/CRUDCardSkeleton';
import {
  useCreateProductPriceOption,
  useDeleteProductPriceOption,
  useUpdateProductPriceOption
} from '../../hooks/products/mutations';
import useCanUseOutsourcedOrdersModule from '../../hooks/useCanUseOutsourcedOrdersModule';
import { IProductSupplies } from '../../interfaces/Product';
import useCanShowFiscalFeature, {
  FiscalFeature
} from '../../hooks/fiscal/useCanShowFiscalFeature';
import { useGetPartners } from '../../hooks/partners/queries';
import { getReferenceId } from '@washday/utilities';
import CatalogAvailabilitySelect from '../../components/CatalogAvailabilitySelect';

const NO_SUBCATEGORY_VALUE = '__none__';

const CreateEditForm = ({
  form,
  storeId,
  isEditing = false,
  deleteProductSupply = () => {},
  createProductSupply = () => {},
  loading = false,
  createProductSupplyLoading = false,
  deleteProductSupplyLoading = false,
  productId,
  refetchProduct,
  canManageCompanyCatalog = false,
  coreReadOnly = false
}: {
  form: any;
  storeId: string;
  isEditing?: boolean;
  deleteProductSupply?: (prodSupplyId: string) => void;
  createProductSupply?: any;
  loading?: boolean;
  createProductSupplyLoading?: boolean;
  deleteProductSupplyLoading?: boolean;
  productId?: string;
  refetchProduct?: () => Promise<any> | void;
  canManageCompanyCatalog?: boolean;
  coreReadOnly?: boolean;
}) => {
  const { t } = useTranslation(['ProductsPage', 'FieldName', 'Notifications']);
  const { company } = useSelector((state) => state.user);

  let rows = [];
  const [formProductSupply, setFormProductSupply] = useState<{
    supplyId: string | null;
    usageAmount: string | number;
  }>({
    supplyId: null,
    usageAmount: 0
  });
  const [variationForm, setVariationForm] = useState<{
    label: string;
    description: string;
    price: number | string;
    expressPrice: number | string;
    isActive: boolean;
    isDefault: boolean;
    editingId?: string | null;
  }>({
    label: '',
    description: '',
    price: '',
    expressPrice: '',
    isActive: true,
    isDefault: false,
    editingId: null
  });
  const [localDefaultId, setLocalDefaultId] = useState<string | null>(null);
  const [isSubmittingVariation, setIsSubmittingVariation] = useState(false);

  const [
    productImagePickerOpened,
    { open: openProductImagePicker, close: closeProductImagePicker }
  ] = useDisclosure(false);
  const catalogStoreId = form.values.store === null
    ? storeId
    : getReferenceId(form.values.store) || storeId;
  const {
    isLoading: isGetSectionsLoading,
    isFetching: isGetSectionsFetching,
    isError: isGetSectionsError,
    data: sections
  } = useGetSections(!!catalogStoreId, {
    storeId: catalogStoreId,
    isActive: true
  });
  const selectedSectionId = getReferenceId(form.values.section) || '';
  const inheritedSectionIdRef = useRef(isEditing ? '' : selectedSectionId);
  const inheritedSectionScopeResolvedRef = useRef(
    isEditing || !inheritedSectionIdRef.current
  );
  const selectedSubcategoryId = getReferenceId(form.values.subcategory);
  const {
    isLoading: isGetSubcategoriesLoading,
    data: subcategories = []
  } = useGetSectionSubcategories(selectedSectionId, Boolean(selectedSectionId));
  const barcodeProps = form.getInputProps('barcode');
  const barcodeInputRef = useRef<HTMLInputElement>(null);
  const [barcodeScannerMode, setBarcodeScannerMode] = useState(false);
  const { isLoading: isGetSuppliesLoading, data: supplies } = useGetSupplies(isEditing, storeId);

  const createOption = useCreateProductPriceOption(productId || '', storeId);
  const updateOption = useUpdateProductPriceOption(productId || '', storeId);
  const deleteOption = useDeleteProductPriceOption(productId || '', storeId);
  const { canUseOutsourcedOrdersModule } = useCanUseOutsourcedOrdersModule(storeId || '');
  const { isLoading: isGetPartnersLoading, data: partners } = useGetPartners(
    canUseOutsourcedOrdersModule
  );
  const canShowMexicanCFDIFields = useCanShowFiscalFeature(FiscalFeature.MEXICO_CFDI_FIELDS);
  const outsourcedInputProps = form.getInputProps('isOutsourced', { type: 'checkbox' });
  const showOutsourcingPartnerSelect =
    canUseOutsourcedOrdersModule && !!form.getValues().isOutsourced;
  const partnerOptions = useMemo(
    () =>
      (partners || []).map((partner) => ({
        label: partner.name,
        value: partner._id
      })),
    [partners]
  );
  const subcategoryOptions = useMemo(() => {
    const activeOptions = (subcategories || []).map((subcategory) => ({
      label: subcategory.name,
      value: subcategory._id || ''
    })).filter((option) => option.value);
    const selectedIsMissing =
      selectedSubcategoryId &&
      !activeOptions.some((option) => option.value === selectedSubcategoryId);

    return [
      {
        label: t('ProductsPage:noSubcategory'),
        value: NO_SUBCATEGORY_VALUE
      },
      ...(selectedIsMissing
        ? [
            {
              label: t('ProductsPage:subcategoryUnavailable'),
              value: selectedSubcategoryId
            }
          ]
        : []),
      ...activeOptions
    ];
  }, [selectedSubcategoryId, subcategories, t]);

  const onRemoveProductimage = () => {
    form.setFieldValue('imageName', '');
    form.setFieldValue('image', null);
  };

  const onIsOutsourcedChange = (event: ChangeEvent<HTMLInputElement>) => {
    outsourcedInputProps.onChange(event);
    if (!event.currentTarget.checked) {
      form.setFieldValue('outsourcingPartner', null);
    }
  };

  const sectionInputProps = form.getInputProps('section');
  const selectedSection = sections?.find((section) => section._id === selectedSectionId);
  const companyWideBlockedByLocalSection = Boolean(
    selectedSection && selectedSection.store !== null
  );
  const selectedSectionScopeUnresolved = Boolean(
    selectedSectionId &&
    (isGetSectionsLoading || isGetSectionsError || !selectedSection)
  );
  const selectedSectionStoreId =
    typeof selectedSection?.store === 'string'
      ? selectedSection.store
      : selectedSection?.store?._id;
  const availableSections = (sections || []).filter(
    (section) => form.values.store !== null || section.store === null
  );
  const setFormFieldValue = form.setFieldValue;

  useEffect(() => {
    if (
      inheritedSectionScopeResolvedRef.current ||
      isEditing ||
      isGetSectionsLoading ||
      isGetSectionsFetching ||
      isGetSectionsError ||
      !Array.isArray(sections)
    ) return;

    const inheritedSection = sections.find(
      (section) => section._id === inheritedSectionIdRef.current
    );
    const fallbackStoreId = canManageCompanyCatalog ? null : storeId;
    inheritedSectionScopeResolvedRef.current = true;

    if (!inheritedSection) {
      setFormFieldValue('store', fallbackStoreId);
      setFormFieldValue('section', '');
      setFormFieldValue('subcategory', null);
      return;
    }

    const inheritedSectionStoreId = inheritedSection.store === null
      ? null
      : typeof inheritedSection.store === 'string'
        ? inheritedSection.store
        : inheritedSection.store?._id;

    if (inheritedSection.store !== null && !inheritedSectionStoreId) {
      setFormFieldValue('store', fallbackStoreId);
      setFormFieldValue('section', '');
      setFormFieldValue('subcategory', null);
      return;
    }

    setFormFieldValue(
      'store',
      canManageCompanyCatalog ? inheritedSectionStoreId : storeId
    );
  }, [
    canManageCompanyCatalog,
    isGetSectionsError,
    isGetSectionsFetching,
    isGetSectionsLoading,
    isEditing,
    sections,
    setFormFieldValue,
    storeId
  ]);

  const onAvailabilityChange = (catalogStoreId: string | null) => {
    if (
      selectedSectionScopeUnresolved ||
      (catalogStoreId === null && companyWideBlockedByLocalSection)
    ) return;
    form.setFieldValue('store', catalogStoreId);
  };

  const submitProductSupply = async () => {
    try {
      if (!formProductSupply.supplyId)
        return showNotification({ type: 'Error', title: 'Por favor seleccione insumo' });
      if (!formProductSupply.usageAmount)
        return showNotification({
          type: 'Error',
          title: 'Por favor asigne monto de uso de insumo'
        });
      await createProductSupply(formProductSupply);
      setFormProductSupply({
        supplyId: null,
        usageAmount: 0
      });
    } catch (e) {
    } finally {
    }
  };

  const suppliesMap = useMemo(() => {
    return supplies?.reduce((map, supply) => {
      map[supply._id] = supply;
      return map;
    }, {});
  }, [supplies]);

  if (isEditing) {
    rows = form
      .getValues()
      .productSupplies?.map(
        (prodSupply: { supplyId: string; usageAmount: number; _id?: string }) => {
          return (
            <Table.Tr key={prodSupply.supplyId}>
              <Table.Td>{suppliesMap?.[prodSupply.supplyId]?.name}</Table.Td>
              <Table.Td>
                {prodSupply.usageAmount}{' '}
                {t(`FieldName:supplyUnits.${suppliesMap?.[prodSupply.supplyId]?.usageUnit}`)}
              </Table.Td>
              <Table.Td>
                {
                  <Tooltip label={t('CommonButtons:delete')}>
                    <ActionIcon
                      variant="transparent"
                      w={'100%'}
                      size={'sm'}
                      loading={deleteProductSupplyLoading}
                      onClick={() =>
                        ConfirmationModal({
                          title: t('Notifications:CRUD.deleteConfirmation'),
                          onConfirm: () => deleteProductSupply(prodSupply.supplyId),
                          onCancel: () => {}
                        })
                      }>
                      <IconTrash />
                    </ActionIcon>
                  </Tooltip>
                }
              </Table.Td>
            </Table.Tr>
          );
        }
      );
  }

  const priceOptions = form.getValues().priceOptions || [];
  const productSupplies = form.getValues().productSupplies || [];
  const hasActiveVariation = (priceOptions || []).some((o: any) => o?.isActive !== false);
  const currentDefaultId =
    (priceOptions || []).find((o: any) => o?.isDefault)?.['_id'] || localDefaultId;

  const renderLabelWithTooltip = (label: string, tooltip: string) => (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 6 }}>
      <span>{label}</span>
      <Tooltip
        label={<span style={{ whiteSpace: 'pre-line' }}>{tooltip}</span>}
        multiline
        maw={280}
        withArrow>
        <ThemeIcon
          component="span"
          size={16}
          radius="xl"
          variant="transparent"
          color="gray"
          aria-label={tooltip}
          style={{ cursor: 'help', flexShrink: 0 }}>
          <IconInfoCircle size={14} stroke={1.8} />
        </ThemeIcon>
      </Tooltip>
    </span>
  );

  const resetVariationForm = () =>
    setVariationForm({
      label: '',
      description: '',
      price: '',
      expressPrice: '',
      isActive: true,
      isDefault: false,
      editingId: null
    });

  const submitCreateOrUpdateVariation = async () => {
    if (!productId) return;
    if (!variationForm.label || variationForm.price === '' || variationForm.expressPrice === '') {
      return showNotification({ type: 'Error', title: 'Completa nombre, precio y precio exprés' });
    }
    try {
      setIsSubmittingVariation(true);
      if (variationForm.editingId) {
        await updateOption.mutateAsync({
          optionId: variationForm.editingId,
          data: {
            label: variationForm.label,
            description: variationForm.description,
            price: Number(variationForm.price),
            expressPrice: Number(variationForm.expressPrice),
            isActive: variationForm.isActive,
            isDefault: variationForm.isDefault
          }
        });
      } else {
        await createOption.mutateAsync({
          label: variationForm.label,
          description: variationForm.description,
          price: Number(variationForm.price),
          expressPrice: Number(variationForm.expressPrice),
          isActive: variationForm.isActive,
          isDefault: variationForm.isDefault
        });
      }
      resetVariationForm();
      await refetchProduct?.();
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    } finally {
      setIsSubmittingVariation(false);
    }
  };

  const onEditOption = (opt: any) => {
    setVariationForm({
      label: opt.label,
      description: opt.description || '',
      price: opt.price,
      expressPrice: opt.expressPrice,
      isActive: opt.isActive !== false,
      isDefault: !!opt.isDefault,
      editingId: opt._id
    });
  };

  const onDeleteOption = async (opt: any) => {
    try {
      if (!productId || !opt?._id) return;
      await deleteOption.mutateAsync(opt._id);
      await refetchProduct?.();
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  const onToggleDefault = async (opt: any) => {
    try {
      if (!productId || !opt?._id) return;
      await updateOption.mutateAsync({ optionId: opt._id, data: { isDefault: true } });
      setLocalDefaultId(opt._id);
      await refetchProduct?.();
      showNotification({ type: 'Default', title: t('Notifications:success') });
    } catch (e) {
      showNotification({ type: 'Error', title: t('Notifications:somethingWrong') });
    }
  };

  useEffect(() => {
    const handleFocus = (e: FocusEvent) => {
      if (
        barcodeInputRef.current &&
        e.target instanceof HTMLElement &&
        e.target !== barcodeInputRef.current
      ) {
        setBarcodeScannerMode(false); // auto-disable scanner
      }
    };

    window.addEventListener('focusin', handleFocus);
    return () => window.removeEventListener('focusin', handleFocus);
  }, []);

  return (
    <div>
      <SelectProductImageModal
        form={form}
        storeId={storeId}
        opened={productImagePickerOpened}
        close={closeProductImagePicker}
      />
      {coreReadOnly && (
        <Text c="dimmed" size="sm" mb="md">
          {t('ProductsPage:availability.readOnlyHelp')}
        </Text>
      )}
      <Grid gutter="md">
        <Grid.Col span={{ xs: 12, md: isEditing ? 6 : 12 }}>
          <Fieldset disabled={coreReadOnly} variant="unstyled" p={0} m={0}>
          {loading || isGetSectionsLoading || isGetSuppliesLoading ? (
            <CRUDCardSkeleton cardContentRows={15} />
          ) : (
            <Card>
              <Card.Section inheritPadding withBorder py="md">
                <Title order={4}> {t('ProductsPage:CRUD.info')}</Title>
              </Card.Section>
              <Card.Section inheritPadding py="md">
                <Grid>
                  <Grid.Col span={12}>
                    {canManageCompanyCatalog ? (
                      <CatalogAvailabilitySelect
                        activeStoreId={storeId}
                        activeStoreFallbackLabel={t(
                          'ProductsPage:availability.currentStore'
                        )}
                        allStoresDisabled={companyWideBlockedByLocalSection}
                        allStoresLabel={t('ProductsPage:availability.allStores')}
                        disabled={selectedSectionScopeUnresolved}
                        errorMessage={t(
                          'ProductsPage:availability.storesLoadError'
                        )}
                        isStoreOptionDisabled={(candidateStoreId) =>
                          Boolean(
                            selectedSectionStoreId &&
                            candidateStoreId !== selectedSectionStoreId
                          )
                        }
                        label={renderLabelWithTooltip(
                          t('ProductsPage:availability.label'),
                          t('ProductsPage:availability.sharedHelp')
                        )}
                        value={form.values.store}
                        onChange={onAvailabilityChange}
                        retryLabel={t('CommonButtons:retry')}
                      />
                    ) : (
                      <>
                        <Text fw={500}>
                          {renderLabelWithTooltip(
                            t('ProductsPage:availability.label'),
                            t('ProductsPage:availability.sharedHelp')
                          )}
                        </Text>
                        <Text size="sm">
                          {form.values.store === null
                            ? t('ProductsPage:availability.allStores')
                            : t('ProductsPage:availability.currentStore')}
                        </Text>
                      </>
                    )}
                    {companyWideBlockedByLocalSection && canManageCompanyCatalog && (
                      <Text size="sm" c="orange" mt="xs">
                        {t('ProductsPage:availability.localSectionRequired')}
                      </Text>
                    )}
                  </Grid.Col>
                  {!isEditing && (
                    <Grid.Col span={12}>
                      <Select
                        {...sectionInputProps}
                        disabled={isEditing}
                        label={t('FieldName:section')}
                        clearable={false}
                        allowDeselect={false}
                        placeholder={t('FieldName:section')}
                        onChange={(value) => {
                          form.setFieldValue('section', value || '');
                          form.setFieldValue('subcategory', null);
                        }}
                        data={
                          availableSections.map((section) => {
                            return {
                              label: section.name,
                              value: section._id
                            };
                          }) || []
                        }
                      />
                    </Grid.Col>
                  )}
                  <Grid.Col span={12}>
                    <Select
                      label={renderLabelWithTooltip(
                        t('FieldName:subcategory'),
                        t('ProductsPage:subcategoryHelp')
                      )}
                      clearable={false}
                      allowDeselect={false}
                      disabled={isGetSubcategoriesLoading}
                      placeholder={t('FieldName:subcategory')}
                      data={subcategoryOptions}
                      value={selectedSubcategoryId || NO_SUBCATEGORY_VALUE}
                      onChange={(value) => {
                        form.setFieldValue(
                          'subcategory',
                          value === NO_SUBCATEGORY_VALUE ? null : value
                        );
                      }}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      label={t('FieldName:name')}
                      placeholder={t('FieldName:name')}
                      {...form.getInputProps('name')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <Select
                      {...form.getInputProps('type')}
                      label={renderLabelWithTooltip(
                        t('FieldName:type'),
                        t('ProductsPage:productTypeHelp')
                      )}
                      clearable={false}
                      allowDeselect={false}
                      placeholder={t('FieldName:type')}
                      data={[
                        { label: t('FieldName:productType.normal'), value: 'normal' },
                        { label: t('FieldName:productType.weight'), value: 'weight' },
                        { label: t('FieldName:productType.length'), value: 'length' }
                      ]}
                    />
                  </Grid.Col>
                  {(form.values.type === 'weight' || form.values.type === 'length') && (
                    <Grid.Col span={{ xs: 12, md: 6 }}>
                      <NumberInput
                        {...form.getInputProps('minimumBillableQuantity')}
                        label={renderLabelWithTooltip(
                          t('ProductsPage:minimumBillableQuantity'),
                          t('ProductsPage:minimumBillableQuantityHelp')
                        )}
                        min={0}
                        decimalScale={3}
                        allowDecimal
                      />
                    </Grid.Col>
                  )}
                  <Grid.Col span={12}>
                    <Textarea
                      {...form.getInputProps('description')}
                      label={renderLabelWithTooltip(
                        t('FieldName:productDescription'),
                        t('ProductsPage:productDescriptionHelp')
                      )}
                      placeholder={t('FieldName:productDescription')}
                      autosize
                      minRows={2}
                      maxRows={4}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <NumberInput
                      {...form.getInputProps('price')}
                      placeholder={t('FieldName:basePrice')}
                      label={t('FieldName:basePrice')}
                      decimalScale={2}
                      prefix="$"
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <NumberInput
                      {...form.getInputProps('expressPrice')}
                      decimalScale={2}
                      placeholder={t('FieldName:priceExpress')}
                      label={renderLabelWithTooltip(
                        t('FieldName:priceExpress'),
                        t('ProductsPage:expressPriceHelp')
                      )}
                      prefix="$"
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <NumberInput
                      {...form.getInputProps('pieces')}
                      placeholder={t('FieldName:pieces')}
                      label={t('FieldName:pieces')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('overlayText')}
                      label={renderLabelWithTooltip(
                        t('FieldName:overlayText'),
                        t('ProductsPage:productOverlayTextHelp')
                      )}
                      placeholder={t('FieldName:overlayText')}
                    />
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...form.getInputProps('sku')}
                      label={renderLabelWithTooltip(
                        t('FieldName:sku'),
                        t('ProductsPage:productSKUHelp')
                      )}
                      placeholder={t('FieldName:sku')}
                    />
                  </Grid.Col>
                  <Grid.Col
                    span={{ xs: 12, md: 6 }}
                    display={'flex'}
                    style={{ alignSelf: 'flex-end' }}>
                    <Button fullWidth variant="outline" onClick={openProductImagePicker}>
                      {form.getValues().imageName || t('FieldName:image')}
                    </Button>
                    <Tooltip label={t('CommonButtons:remove')}>
                      <ActionIcon
                        disabled={!form.getValues().image}
                        radius={0}
                        variant="transparent"
                        color="red"
                        w={'auto'}
                        onClick={() =>
                          ConfirmationModal({
                            title: t('Notifications:CRUD.removeConfirmation'),
                            onConfirm: () => {
                              onRemoveProductimage();
                            },
                            onCancel: () => {}
                          })
                        }>
                        <IconTrash />
                      </ActionIcon>
                    </Tooltip>
                  </Grid.Col>
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <NumberInput
                      {...form.getInputProps('order')}
                      allowDecimal={false}
                      label={renderLabelWithTooltip(
                        t('FieldName:displayedOrder'),
                        t('ProductsPage:orderDisplayedHelp')
                      )}
                      placeholder={t('FieldName:displayedOrder')}
                    />
                  </Grid.Col>

                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <TextInput
                      {...barcodeProps}
                      ref={barcodeInputRef}
                      label={renderLabelWithTooltip(
                        t('FieldName:barcode'),
                        t('ProductsPage:barcodeHelp')
                      )}
                      placeholder={t('FieldName:barcode')}
                      onBlur={(e) => {
                        const normalized = e.target.value.trim().toLowerCase();
                        form.setFieldValue('barcode', normalized);
                        barcodeProps.onBlur?.(e);
                        setBarcodeScannerMode(false);
                      }}
                      onKeyDown={(e) => {
                        if (e.key === 'Enter') {
                          e.preventDefault();
                        }
                      }}
                      rightSection={
                        <Tooltip
                          label={
                            barcodeScannerMode
                              ? t('CommonButtons:disableScanner')
                              : t('CommonButtons:enableScanner')
                          }>
                          <ActionIcon
                            variant={barcodeScannerMode ? 'filled' : 'light'}
                            color={barcodeScannerMode ? 'blue' : 'gray'}
                            onClick={() => {
                              setBarcodeScannerMode(!barcodeScannerMode);
                              if (!barcodeScannerMode) {
                                setTimeout(() => barcodeInputRef.current?.focus(), 100);
                              }
                            }}>
                            <IconBarcode />
                          </ActionIcon>
                        </Tooltip>
                      }
                    />
                  </Grid.Col>
                  {canUseOutsourcedOrdersModule && (
                    <>
                      <Grid.Col span={{ xs: 12, md: 6 }}>
                        <Checkbox
                          {...outsourcedInputProps}
                          onChange={onIsOutsourcedChange}
                          label={renderLabelWithTooltip(
                            t('FieldName:isOutsourced'),
                            'Esta configuración decidira si este producto o servicio se realiza por medio de un tercero (maquilación)'
                          )}
                        />
                      </Grid.Col>
                      {showOutsourcingPartnerSelect && (
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                          <Select
                            {...form.getInputProps('outsourcingPartner')}
                            label={renderLabelWithTooltip(
                              t('FieldName:outsourcingPartner'),
                              t('ProductsPage:outsourcingPartnerHelp')
                            )}
                            placeholder={t('ProductsPage:outsourcingPartnerPlaceholder')}
                            data={partnerOptions}
                            searchable
                            clearable
                            disabled={isGetPartnersLoading}
                          />
                        </Grid.Col>
                      )}
                      <Grid.Col span={12}>
                        <Text size="sm" c="dimmed">
                          {t('ProductsPage:outsourcingGuideHelp')}{' '}
                          <a href={outsourcingOrdersGuide} target="_blank" rel="noopener noreferrer">
                            {t('ProductsPage:outsourcingGuideLink')}
                          </a>
                        </Text>
                      </Grid.Col>
                    </>
                  )}
                  <Grid.Col span={{ xs: 12, md: 6 }}>
                    <Flex justify={'space-between'} align={'center'} wrap={'wrap'} gap={'md'}>
                      <Checkbox
                        {...form.getInputProps('taxExemptOne', { type: 'checkbox' })}
                        label={renderLabelWithTooltip(
                          t('FieldName:taxExemptOne'),
                          'Al crear un pedido con este producto, se ignorara el impuesto 1 configurado en el calculo del total del pedido para este producto'
                        )}
                      />
                      <Checkbox
                        {...form.getInputProps('taxExemptTwo', { type: 'checkbox' })}
                        label={renderLabelWithTooltip(
                          t('FieldName:taxExemptTwo'),
                          'Al crear un pedido con este producto, se ignorara el impuesto 2 configurado en el calculo del total del pedido para este producto'
                        )}
                      />
                      <Checkbox
                        {...form.getInputProps('taxExemptThree', { type: 'checkbox' })}
                        label={renderLabelWithTooltip(
                          t('FieldName:taxExemptThree'),
                          'Al crear un pedido con este producto, se ignorara el impuesto 3 configurado en el calculo del total del pedido para este producto'
                        )}
                      />
                    </Flex>
                  </Grid.Col>
                  {company?.companyServicesPermissions?.canUseCustomersApp && ( //TEMPORALLY HIDDEN UNTIL WE HAVE CUSTOMER APPS
                    <Grid.Col span={12}>
                      <Checkbox
                        {...form.getInputProps('showInApp', { type: 'checkbox' })}
                        label={renderLabelWithTooltip(
                          t('FieldName:showInApp'),
                          t('ProductsPage:showInAppHelp')
                        )}
                      />
                    </Grid.Col>
                  )}
                </Grid>
              </Card.Section>
            </Card>
          )}
          </Fieldset>
        </Grid.Col>
        {isEditing && (
          <Grid.Col span={{ xs: 12, md: 6 }} p={0}>
            {canShowMexicanCFDIFields && (
              <Grid.Col span={12}>
                <Fieldset disabled={coreReadOnly} variant="unstyled" p={0} m={0}>
                {loading || isGetSuppliesLoading || isGetSectionsLoading ? (
                  <CRUDCardSkeleton cardContentRows={10} />
                ) : (
                  <Card>
                    <Card.Section inheritPadding withBorder py="md">
                      <Title order={4}> {t('ProductsPage:CRUD.invoice')}</Title>
                      <Text size="sm" c="dimmed">
                        Puedes obtener mas información sobre el modulo de facturación en nuestras
                        guías aqui:{' '}
                        <a href={cfdiInvoiceGuideFolder} target="_blank">
                          CFDI (Mexico)
                        </a>
                      </Text>
                    </Card.Section>
                    <Card.Section inheritPadding py="md">
                      <Grid>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                          <TextInput
                            {...form.getInputProps('invoice_product_key')}
                            label={t('FieldName:invoice_product_key')}
                            placeholder={t('FieldName:invoice_product_key')}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                          <TextInput
                            {...form.getInputProps('invoice_description')}
                            label={t('FieldName:invoice_description')}
                            placeholder={t('FieldName:invoice_description')}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                          <TextInput
                            {...form.getInputProps('invoice_product_unit_key')}
                            label={t('FieldName:invoice_product_unit_key')}
                            placeholder={t('FieldName:invoice_product_unit_key')}
                          />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, md: 6 }}>
                          <TextInput
                            {...form.getInputProps('invoice_product_unit_name')}
                            label={t('FieldName:invoice_product_unit_name')}
                            placeholder={t('FieldName:invoice_product_unit_name')}
                            maxLength={20}
                          />
                        </Grid.Col>
                      </Grid>
                    </Card.Section>
                  </Card>
                )}
                </Fieldset>
              </Grid.Col>
            )}
            <Grid.Col span={12}>
              <Fieldset disabled={coreReadOnly} variant="unstyled" p={0} m={0}>
              {loading || isGetSuppliesLoading || isGetSectionsLoading ? (
                <CRUDCardSkeleton cardContentRows={10} />
              ) : (
                <Card>
                  <Card.Section inheritPadding withBorder py="md">
                    <Title order={4}> {t('ProductsPage:CRUD.variations')}</Title>
                    <Text size="sm" c="dimmed" mt="xs">
                      {t('ProductsPage:variationsHelp')}
                    </Text>
                    <Text size="sm" c="dimmed" mt="sm">
                      <a href={howToSetUpProductVariations} target="_blank">
                        {t('ProductsPage:variationsHelpLink')}
                      </a>
                    </Text>
                  </Card.Section>
                  <Card.Section inheritPadding pt="lg" pb="md">
                    <Stack gap="md">
                      <TextInput
                        label={t('FieldName:name')}
                        placeholder={t('FieldName:name')}
                        value={variationForm.label}
                        onChange={(e) =>
                          setVariationForm({
                            ...variationForm,
                            label: e.currentTarget.value
                          })
                        }
                      />

                      <Textarea
                        label={t('FieldName:variantDescription')}
                        placeholder={t('FieldName:variantDescription')}
                        value={variationForm.description}
                        onChange={(e) =>
                          setVariationForm({
                            ...variationForm,
                            description: e.currentTarget.value
                          })
                        }
                        autosize
                        minRows={2}
                        maxRows={2}
                      />

                      <Grid gutter="md" align="flex-end">
                        <Grid.Col span={{ xs: 12, sm: 6 }}>
                          <NumberInput
                            allowDecimal
                            label={t('FieldName:price')}
                            placeholder={t('FieldName:price')}
                            value={variationForm.price as any}
                            onChange={(value) =>
                              setVariationForm({ ...variationForm, price: value || '' })
                            }
                            decimalScale={2}
                            prefix="$"
                          />
                        </Grid.Col>
                        <Grid.Col span={{ xs: 12, sm: 6 }}>
                          <NumberInput
                            allowDecimal
                            label={t('FieldName:priceExpress')}
                            placeholder={t('FieldName:priceExpress')}
                            value={variationForm.expressPrice as any}
                            onChange={(value) =>
                              setVariationForm({
                                ...variationForm,
                                expressPrice: value || ''
                              })
                            }
                            decimalScale={2}
                            prefix="$"
                          />
                        </Grid.Col>
                      </Grid>

                      <Flex justify="flex-start">
                        <Button
                          loading={isSubmittingVariation}
                          miw={200}
                          size="md"
                          onClick={submitCreateOrUpdateVariation}>
                          {variationForm.editingId
                            ? t('CommonButtons:save')
                            : t('ProductsPage:addVariation')}
                        </Button>
                      </Flex>
                    </Stack>
                    <Divider mt="md" mb="lg" />
                    <Table.ScrollContainer minWidth={760}>
                      <Table striped highlightOnHover withTableBorder withColumnBorders>
                        <Table.Thead>
                          <Table.Tr>
                            <Table.Th>{t('FieldName:actions')}</Table.Th>
                            <Table.Th>{t('FieldName:name')}</Table.Th>
                            <Table.Th>{t('FieldName:variantDescription')}</Table.Th>
                            <Table.Th>{t('FieldName:price')}</Table.Th>
                            <Table.Th>{t('FieldName:priceExpress')}</Table.Th>
                            {/* <Table.Th>Default</Table.Th> */}
                            {/* <Table.Th>Activo</Table.Th> */}
                          </Table.Tr>
                        </Table.Thead>
                        <Table.Tbody>
                          {(priceOptions || []).map((opt: any) => (
                            <Table.Tr key={opt._id}>
                              <Table.Td>
                                <Group gap="xs">
                                  <ActionIcon variant="subtle" onClick={() => onEditOption(opt)}>
                                    <IconPencil size={16} />
                                  </ActionIcon>
                                  <ActionIcon
                                    color="red"
                                    variant="subtle"
                                    onClick={() =>
                                      ConfirmationModal({
                                        title: t('Notifications:CRUD.deleteConfirmation'),
                                        onConfirm: () => onDeleteOption(opt),
                                        onCancel: () => {}
                                      })
                                    }>
                                    <IconTrash size={16} />
                                  </ActionIcon>
                                </Group>
                              </Table.Td>
                              <Table.Td>{opt.label}</Table.Td>
                              <Table.Td>{opt.description || '-'}</Table.Td>
                              <Table.Td>${Number(opt.price).toFixed(2)}</Table.Td>
                              <Table.Td>${Number(opt.expressPrice).toFixed(2)}</Table.Td>
                              {/* <Table.Td>
                                <Radio
                                  name="defaultOption"
                                  checked={currentDefaultId === opt._id}
                                  onChange={() => onToggleDefault(opt)}
                                />
                              </Table.Td> */}
                              {/* <Table.Td>
                                <Switch checked={opt.isActive !== false} disabled readOnly />
                              </Table.Td> */}
                            </Table.Tr>
                          ))}
                        </Table.Tbody>
                      </Table>
                    </Table.ScrollContainer>
                  </Card.Section>
                </Card>
              )}
              </Fieldset>
            </Grid.Col>
            <Grid.Col span={12}>
              {loading || isGetSuppliesLoading || isGetSectionsLoading ? (
                <CRUDCardSkeleton cardContentRows={10} />
              ) : (
                <Card>
                  <Card.Section inheritPadding withBorder py="md">
                    <Title order={4}> {t('ProductsPage:CRUD.supplies')}</Title>
                  </Card.Section>
                  <Card.Section inheritPadding withBorder py="md">
                    <Grid align="flex-end">
                      <Grid.Col span={{ xs: 12, md: 4 }}>
                        <Select
                          label={t('FieldName:supply')}
                          value={formProductSupply.supplyId}
                          onChange={(value) => {
                            setFormProductSupply({
                              ...formProductSupply,
                              supplyId: value
                            });
                          }}
                          placeholder={t('FieldName:supply')}
                          data={supplies?.map((supply) => {
                            return {
                              value: supply._id || '',
                              label: supply.name || ''
                            };
                          })}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ xs: 12, md: 4 }}>
                        <NumberInput
                          allowDecimal={true}
                          allowLeadingZeros={false}
                          allowNegative={false}
                          value={formProductSupply.usageAmount}
                          label={t('FieldName:usageAmount')}
                          placeholder={t('FieldName:usageAmount')}
                          onChange={(value) => {
                            setFormProductSupply({
                              ...formProductSupply,
                              usageAmount: value
                            });
                          }}
                        />
                      </Grid.Col>
                      <Grid.Col span={{ xs: 12, md: 4 }}>
                        <Button
                          fullWidth
                          onClick={submitProductSupply}
                          loading={createProductSupplyLoading}>
                          {t('CommonButtons:add')}
                        </Button>
                      </Grid.Col>
                    </Grid>
                  </Card.Section>
                  <Card.Section inheritPadding py="md">
                    <Grid>
                      <Grid.Col span={12}>
                        <Table.ScrollContainer minWidth={'100%'}>
                          <Table striped highlightOnHover withTableBorder withColumnBorders>
                            <Table.Thead>
                              <Table.Tr>
                                <Table.Th>{t('FieldName:supply')}</Table.Th>
                                <Table.Th>{t('FieldName:quantity')}</Table.Th>
                                <Table.Th>{t('FieldName:actions')}</Table.Th>
                              </Table.Tr>
                            </Table.Thead>
                            <Table.Tbody>
                              {rows}
                            </Table.Tbody>
                          </Table>
                        </Table.ScrollContainer>
                      </Grid.Col>
                    </Grid>
                  </Card.Section>
                </Card>
              )}
            </Grid.Col>
          </Grid.Col>
        )}
      </Grid>
    </div>
  );
};

export default CreateEditForm;
